import 'react-day-picker/style.css';
import '~/themes/calendar.scss';

import { addDays, format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { DayPicker, useDayPicker, type MonthCaptionProps } from 'react-day-picker';
import {
  OutlineChevronDownIcon,
  OutlineChevronLeftIcon,
  OutlineChevronRightIcon,
} from '@tempmee/react-icons';
import { Button, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { ZStack } from '~/commonComponents';
import { HeadRow } from '~/components/Request/multiday/components/Rows/DateRow/HeadRow';

const ARROW_BUTTON_SIZE = 38;
const ARROW_BORDER_RADIUS = 6;
const ACTIVE_BOX_SHADOW = '0px 1px 2px rgba(0,0,0,0.15), 0px 3px 10px 1px rgba(0,0,0,0.15)';
const HOVER_BOX_SHADOW = '0px 2px 4px rgba(0,0,0,0.2), 0px 4px 14px 2px rgba(0,0,0,0.2)';

const arrowButtonStyle = (disabled: boolean) => ({
  all: 'unset' as const,
  display: 'flex',
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  width: ARROW_BUTTON_SIZE,
  height: ARROW_BUTTON_SIZE,
  borderRadius: ARROW_BORDER_RADIUS,
  backgroundColor: disabled ? colors.secondary[200] : colors.secondary[500],
  boxShadow: disabled ? 'none' : ACTIVE_BOX_SHADOW,
  cursor: disabled ? ('default' as const) : ('pointer' as const),
  pointerEvents: disabled ? ('none' as const) : ('auto' as const),
  transition: 'transform 0.2s ease',
});

const handleArrowHover = (e: React.MouseEvent<HTMLButtonElement>, disabled: boolean) => {
  if (!disabled) {
    e.currentTarget.style.transform = 'scale(1.1)';
    e.currentTarget.style.backgroundColor = colors.secondary[350];
    e.currentTarget.style.boxShadow = HOVER_BOX_SHADOW;
  }
};

const handleArrowLeave = (e: React.MouseEvent<HTMLButtonElement>, disabled: boolean) => {
  e.currentTarget.style.transform = 'scale(1)';
  e.currentTarget.style.backgroundColor = disabled ? colors.secondary[200] : colors.secondary[500];
  e.currentTarget.style.boxShadow = disabled ? 'none' : ACTIVE_BOX_SHADOW;
};

function SingleMonthNavigation({ calendarMonth }: MonthCaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();
  const displayMonth = calendarMonth.date;
  const isPrevDisabled = !previousMonth;
  const isNextDisabled = !nextMonth;

  return (
    <Row align="center" style={{ gap: 10 }}>
      <button
        type="button"
        data-testid="month-nav-prev"
        data-cy="month-nav-prev"
        disabled={isPrevDisabled}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        style={arrowButtonStyle(isPrevDisabled)}
        onMouseEnter={(e) => handleArrowHover(e, isPrevDisabled)}
        onMouseLeave={(e) => handleArrowLeave(e, isPrevDisabled)}
      >
        <OutlineChevronLeftIcon color={colors.shades[0]} />
      </button>

      <Typography
        as="span"
        data-testid="month-nav-label"
        data-cy="month-nav-label"
        bold
        size="heading_4"
        textAlign="center"
        style={{
          flex: 1,
          backgroundColor: colors.secondary[50],
          borderRadius: 6,
          padding: '4px 12px',
        }}
      >
        {format(displayMonth, 'MMMM')}
      </Typography>

      <button
        type="button"
        data-testid="month-nav-next"
        data-cy="month-nav-next"
        disabled={isNextDisabled}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        style={arrowButtonStyle(isNextDisabled)}
        onMouseEnter={(e) => handleArrowHover(e, isNextDisabled)}
        onMouseLeave={(e) => handleArrowLeave(e, isNextDisabled)}
      >
        <OutlineChevronRightIcon color={colors.shades[0]} />
      </button>
    </Row>
  );
}

interface IDatePickerPopupProps {
  currentDate: Date;
  isOpen: boolean;
  onToggle: () => void;
  onConfirm: (date: Date) => void;
}

export const DatePickerPopup = ({
  currentDate,
  isOpen,
  onToggle,
  onConfirm,
}: IDatePickerPopupProps) => {
  const [pendingDate, setPendingDate] = useState<Date>(currentDate);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setPendingDate(currentDate);
    }
  }, [isOpen, currentDate]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onToggle();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  if (!isOpen) return null;

  const today = new Date();

  const handleConfirm = () => {
    onConfirm(pendingDate);
  };

  return (
    <Column
      ref={popupRef}
      data-testid="date-picker-popup"
      data-cy="date-picker-popup"
      align="center"
      style={{
        backgroundColor: colors.shades[0],
        border: `1.5px solid ${colors.neutral[100]}`,
        borderRadius: 12,
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
        position: 'absolute',
        top: '100%',
        left: '-12%',
        transform: 'translateX(-50%)',
        marginTop: 8,
        zIndex: 4,
        padding: '20px 36px',
      }}
    >
      <Row
        align="center"
        justify="center"
        width="100%"
        style={{ cursor: 'pointer', marginBottom: 8, marginTop: 16, position: 'relative' }}
        onClick={onToggle}
        data-testid="date-picker-header"
        data-cy="date-picker-header"
      >
        <Typography as="span" bold size="heading_4" color={colors.primary[500]}>
          Select Date
        </Typography>
        <ZStack
          position="absolute"
          right={-16}
          top={-16}
          style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}
        >
          <OutlineChevronDownIcon size={24} color={colors.secondary[500]} />
        </ZStack>
      </Row>

      <Column align="center">
        <DayPicker
          data-testid="date-picker-calendar"
          data-cy="date-picker-calendar"
          mode="single"
          numberOfMonths={1}
          selected={pendingDate}
          onSelect={(date) => date && setPendingDate(date)}
          startMonth={today}
          components={{
            MonthCaption: SingleMonthNavigation,
            Weekdays: HeadRow,
          }}
          disabled={[
            {
              before: today,
              after: addDays(today, 99),
            },
          ]}
        />
      </Column>

      <Button
        variant="contained"
        size="md"
        data-testid="date-picker-confirm"
        data-cy="date-picker-confirm"
        onClick={handleConfirm}
        style={{ marginTop: 8, marginBottom: 16 }}
      >
        Confirm
      </Button>
    </Column>
  );
};
